import {
  Alert,
  Button,
  ButtonLink,
  DownloadIcon,
  PlusIcon,
  SelectField,
  Stack,
  TablePager,
  TextInputField,
} from '@fukuri-admins/ui/common';
import { ClientsTable } from './ClientsTable';
import styles from './styles.module.scss';

/** 与信状態セレクトの選択肢 */
const CREDIT_GRANTED_OPTIONS = [
  { value: '0', label: '未完了' },
  { value: '1', label: '完了' },
];

type Props = {
  /** テーブルに表示する利用企業行データ */
  rows: React.ComponentProps<typeof ClientsTable>['rows'];
  /**
   * カスタムフィールド列定義（optional）
   *
   * 定義がある場合はグループ列と取引店舗列の間に挿入される
   */
  customFieldColumns?: React.ComponentProps<typeof ClientsTable>['customFieldColumns'];
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param clientId 操作対象の利用企業 ID
   */
  onEditClient: (clientId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param row 操作対象の利用企業行データ（削除ダイアログに5項目を渡すため row 全体を渡す）
   */
  onDeleteClient: React.ComponentProps<typeof ClientsTable>['onDeleteClient'];
  /**
   * 行クリック時のコールバック（利用企業詳細画面への遷移）
   *
   * @param clientId 操作対象の利用企業 ID
   */
  onRowClick: (clientId: number) => void;
  /** ページャー props */
  pagerProps: React.ComponentProps<typeof TablePager>;
  /** 会社名検索フォームの値 */
  nameValue: string;
  /**
   * 会社名検索フォーム変更時のコールバック
   *
   * @param value 入力値
   */
  onChangeName: (value: string) => void;
  /** 与信状態セレクトの値（'0': 未完了, '1': 完了, '': 選択なし） */
  creditGrantedValue: string;
  /**
   * 与信状態セレクト変更時のコールバック
   *
   * @param value 選択値
   */
  onChangeCreditGranted: (value: string) => void;
  /**
   * 与信評価機能フラグ
   *
   * true のとき与信状態セレクト・与信状況列・未与信件数アラートを表示する
   */
  creditEvaluationEnabled: boolean;
  /**
   * マスタ取引店舗一覧（GET /api/v1/{threeLetterCode}/partner_admins/partner_branches）に
   * 1件以上の取引店舗が存在するか（PEN-985）
   *
   * true のときのみ ClientsTable の取引店舗列を表示する（PEN-989）
   */
  hasPartnerBranch: boolean;
  /**
   * 未与信件数
   *
   * creditEvaluationEnabled=true かつ ungrantedCreditCount > 0 のとき警告アラートを表示する
   */
  ungrantedCreditCount: number;
  /**
   * 与信確認ボタン押下時のコールバック
   *
   * @param clientId 操作対象の利用企業 ID
   */
  onGrantCredit: (clientId: number) => void;
  /** 検索ボタン押下時のコールバック */
  onSearch: () => void;
  /** 条件をリセットボタン押下時のコールバック */
  onReset: () => void;
  /** CSV ダウンロードボタン押下時のコールバック */
  onDownloadCsv: () => void;
  /**
   * CSV ダウンロード中フラグ
   *
   * true のときダウンロードボタンを disabled にして多重クリックを防ぐ
   */
  isDownloadingCsv?: boolean;
  /** 「利用者数」ヘルプアイコンのツールチップ表示ハンドラー */
  showTooltip: React.ComponentProps<typeof ClientsTable>['showTooltip'];
  /** ツールチップを非表示にするハンドラー */
  hideTooltip: React.ComponentProps<typeof ClientsTable>['hideTooltip'];
  /** ツールチップの id */
  tooltipId: React.ComponentProps<typeof ClientsTable>['tooltipId'];
};

/**
 * 利用企業一覧 Presentation
 *
 * - 未与信件数アラート（creditEvaluationEnabled かつ ungrantedCreditCount > 0 のとき表示）
 * - 上段に会社名検索フォーム（TextInputField）・与信状態セレクト（creditEvaluationEnabled 時のみ表示）・
 *   検索ボタン・条件リセットボタンを配置する
 * - 上段右側に CSV ダウンロードボタン・新規追加ボタンを配置する
 * - ClientsTable にテーブル本体を委譲する（列定義は columns で動的に渡す）
 * - TablePager を配置してページネーション UI を提供する
 * - CSV ダウンロードの動作配線は T06（PEN-565）で実装する
 * - hasPartnerBranch のときのみ ClientsTable の取引店舗列を表示する（PEN-985 / PEN-989）
 */
export const ClientListPresentation: React.FC<Props> = ({
  rows,
  customFieldColumns,
  onEditClient,
  onDeleteClient,
  onRowClick,
  pagerProps,
  nameValue,
  onChangeName,
  creditGrantedValue,
  onChangeCreditGranted,
  creditEvaluationEnabled,
  hasPartnerBranch,
  ungrantedCreditCount,
  onGrantCredit,
  onSearch,
  onReset,
  onDownloadCsv,
  isDownloadingCsv = false,
  showTooltip,
  hideTooltip,
  tooltipId,
}) => (
  <Stack direction='column' gap={16}>
    {creditEvaluationEnabled && ungrantedCreditCount > 0 && (
      <Alert
        status='warning'
        description={`与信が完了していない利用企業が ${ungrantedCreditCount} 社あります。`}
      />
    )}
    <Stack direction='row' justifyContent='space-between' alignItems='flex-end'>
      <Stack direction='row' gap={8} alignItems='flex-end'>
        <div className={styles.searchField}>
          <TextInputField
            fieldWrapperProps={{
              htmlFor: 'client-list-name-input',
              labelText: '会社名',
            }}
            textInput={{
              placeholder: '会社名を入力',
              value: nameValue,
              onChange: e => onChangeName(e.target.value),
            }}
          />
        </div>
        {creditEvaluationEnabled && (
          <div className={styles.searchField}>
            <SelectField
              fieldWrapperProps={{
                htmlFor: 'client-list-credit-granted-select',
                labelText: '与信状態',
              }}
              select={{
                placeholder: '選択してください',
                value: creditGrantedValue || undefined,
                options: CREDIT_GRANTED_OPTIONS,
                onValueChange: onChangeCreditGranted,
                clearable: true,
              }}
            />
          </div>
        )}
        <Button variant='secondary' type='button' onClick={onSearch}>
          検索
        </Button>
        <Button variant='ghost' type='button' onClick={onReset}>
          条件をリセット
        </Button>
      </Stack>
      <Stack direction='row' gap={8} alignItems='flex-end'>
        <Button
          variant='outline'
          type='button'
          onClick={onDownloadCsv}
          icon={<DownloadIcon size='xs' />}
          iconPosition='left'
          disabled={isDownloadingCsv}
        >
          ダウンロード
        </Button>
        <ButtonLink
          variant='primary'
          size='md'
          icon={<PlusIcon size='xs' />}
          iconPosition='left'
          to='/clients/new'
        >
          新規追加
        </ButtonLink>
      </Stack>
    </Stack>
    <ClientsTable
      rows={rows}
      customFieldColumns={customFieldColumns}
      creditEvaluationEnabled={creditEvaluationEnabled}
      hasPartnerBranch={hasPartnerBranch}
      onEditClient={onEditClient}
      onDeleteClient={onDeleteClient}
      onRowClick={onRowClick}
      onGrantCredit={onGrantCredit}
      showTooltip={showTooltip}
      hideTooltip={hideTooltip}
      tooltipId={tooltipId}
    />
    <TablePager {...pagerProps} />
  </Stack>
);
